import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check } from "lucide-react";
import CaseStudyCard from "@/components/CaseStudyCard";
import Markdown from "@/components/Markdown";
// NOTE: Lightbox is intentionally NOT imported here — the staged
// case-study Lightbox feature is disconnected from production
// until it's separately approved. Component file remains at
// /app/frontend/src/components/Lightbox.jsx.
import SEO, { ORGANISATION_SCHEMA, SITE_URL } from "@/components/SEO";
import { API } from "@/lib/apiBase";

// Inline-bold renderer for **text** within a paragraph
const renderInlineBold = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="text-white font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Lightbox state removed — staged feature disconnected from production.

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await axios.get(`${API}/services/${slug}`);
        setService(response.data);
      } catch (error) {
        // Only treat an explicit 404 as "service doesn't exist".
        // Network/CORS/timeout errors must NOT render a 404-style page or
        // search-engine crawlers will index this URL as missing.
        if (error?.response?.status === 404) {
          setNotFound(true);
        } else {
          console.error("Error fetching service:", error);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [slug]);

  if (loading || (!service && !notFound)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-ink">
        <h1 className="font-heading text-3xl font-bold text-white mb-4">
          Service Not Found
        </h1>
        <Link to="/services" className="btn-secondary">
          Back to Services
        </Link>
      </div>
    );
  }

  return (
    <div className="page-wrapper bg-ink" data-testid="service-detail-page">
      <SEO
        title={service.title}
        description={service.description}
        path={`/services/${service.slug}`}
        image={service.image_url}
        keywords={`${service.title.toLowerCase()} auckland, ${service.title.toLowerCase()} resource consent, code planning, planning consultant auckland`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: service.title,
            description: service.long_description || service.description,
            url: `${SITE_URL}/services/${service.slug}`,
            image: service.image_url,
            provider: { "@id": `${SITE_URL}/#organization` },
            areaServed: { "@type": "AdministrativeArea", name: "Auckland, New Zealand" },
            serviceType: "Resource consent and planning service",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services` },
              { "@type": "ListItem", position: 3, name: service.title, item: `${SITE_URL}/services/${service.slug}` },
            ],
          },
          ORGANISATION_SCHEMA,
        ]}
      />
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-end">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={service.image_url}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14 w-full">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors duration-200"
            data-testid="back-to-services"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm uppercase tracking-widest">All Services</span>
          </Link>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white">
            {service.title}
          </h1>
          {service.tagline && (
            <p
              className="mt-4 font-heading text-2xl md:text-3xl lg:text-4xl italic text-gray-200"
              data-testid="service-tagline"
            >
              {service.tagline}
            </p>
          )}
        </div>
      </section>

      {/* Description */}
      <section className="py-10 md:py-14 bg-ink">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-lg md:text-xl text-white leading-relaxed mb-8">
              {service.description}
            </p>
            <Markdown className="text-gray-400 leading-relaxed [&_p]:text-gray-400">
              {service.long_description}
            </Markdown>
          </div>
        </div>
      </section>

      {/* Extra Sections (e.g., The Problem / Our Solution) */}
      {service.extra_sections && service.extra_sections.length > 0 && (
        <section className="py-10 md:py-14 bg-neutral-900" data-testid="extra-sections">
          <div className="max-w-5xl mx-auto px-6 md:px-12 space-y-16">
            {service.extra_sections.map((section, idx) => (
              <div key={idx} data-testid={`extra-section-${idx}`}>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-8">
                  {section.title}
                </h2>
                <div className="space-y-5 max-w-3xl">
                  {section.body.split("\n\n").map((para, i) => (
                    <p key={i} className="text-gray-300 text-lg leading-relaxed">
                      {renderInlineBold(para)}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Client List */}
      {service.client_list && service.client_list.length > 0 && (
        <section className="py-10 md:py-14 bg-ink border-t border-neutral-900" data-testid="client-list-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-12">
              <p className="text-sm uppercase tracking-widest text-gray-400 mb-3">
                Trusted By
              </p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white">
                Current Industry Clients
              </h2>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
              {service.client_list.map((client, idx) => (
                <li
                  key={idx}
                  className="flex items-center gap-3 text-white text-lg font-medium border-b border-neutral-800 pb-3"
                  data-testid={`client-${idx}`}
                >
                  <Check className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  {client}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Case Study — Featured layout */}
      {service.case_studies && service.case_studies.length > 0 && (
        <section className="py-10 md:py-14 bg-neutral-900" data-testid="case-study-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-8 md:mb-10">
              <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">
                Project Experience
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white">
                {service.case_studies.length > 1 ? "Case Studies" : "Case Study"}
              </h2>
            </div>

            <div className="space-y-12 md:space-y-16">
            {service.case_studies.map((cs) => (
              <div key={cs.id} data-testid="featured-case-study">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                  <h3 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-white max-w-3xl">
                    {cs.title}
                  </h3>
                  <p className="text-sm uppercase tracking-widest text-gray-400 flex-shrink-0">
                    {cs.location}
                  </p>
                </div>

                {/* Hero Image — Lightbox disabled while feature is staged */}
                {cs.image_url && (
                  <div
                    className="w-full aspect-[16/9] overflow-hidden mb-12 bg-ink border border-neutral-800 group"
                    data-testid="case-study-hero-image"
                  >
                    <img
                      src={cs.image_url}
                      alt={cs.title}
                      loading="lazy"
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                  </div>
                )}

                {/* Body */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-12">
                  <div>
                    <h4 className="text-sm uppercase tracking-widest font-bold text-white mb-4">
                      Challenge
                    </h4>
                    <Markdown>{cs.challenge}</Markdown>
                  </div>
                  <div>
                    <h4 className="text-sm uppercase tracking-widest font-bold text-white mb-4">
                      Solution
                    </h4>
                    <Markdown>{cs.solution}</Markdown>
                  </div>
                  <div className="border-l-4 border-white pl-6">
                    <h4 className="text-sm uppercase tracking-widest font-bold text-white mb-4">
                      Outcome
                    </h4>
                    <Markdown className="[&_p]:text-white [&_p]:font-medium">{cs.outcome}</Markdown>
                  </div>
                </div>

                {/* Gallery — Lightbox disabled while feature is staged.
                    Any case studies with populated gallery arrays will render
                    the thumbnails inline (non-clickable) rather than nothing. */}
                {cs.gallery && cs.gallery.length > 0 && (
                  <div data-testid="case-study-gallery">
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">
                      Project Gallery
                    </p>
                    <div className={`grid gap-4 ${cs.gallery.length >= 3 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2"}`}>
                      {cs.gallery.map((url, i) => (
                        <div
                          key={i}
                          className="aspect-[4/3] overflow-hidden bg-ink border border-neutral-800 group"
                          data-testid={`gallery-image-${i}`}
                        >
                          <img
                            src={url}
                            alt={`${cs.title} — ${i + 1}`}
                            loading="lazy"
                            className="w-full h-full object-contain grayscale group-hover:grayscale-0 transition-all duration-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-10 md:py-14 bg-ink">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Need Help With Your {service.title} Project?
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-8">
            Request a Free Property Planning Review — we'll provide an initial
            indication of the likely consent pathway, cost and timeframe based
            on the information available.
          </p>
          <Link to="/contact#property-form" className="btn-primary inline-block" data-testid="service-cta">
            Check My Property
          </Link>
        </div>
      </section>

      {/* Lightbox component is disconnected from production while staged. */}
    </div>
  );
}
